/* IrisChat — flirty (but tasteful) AI girlfriend chat under Secret tab.
 * PIN-locked via parent SecretNotes. Lives ONLY when unlocked. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Settings, Trash2, Loader2, X, Heart, Sparkles, Mail, Upload } from 'lucide-react';
import IrisSetupWizard from './IrisSetupWizard';
import PortraitCropModal from './PortraitCropModal';
import { notifyLlmFailure } from './LlmBudgetBanner';

const looksLikeLlmError = (msg) => {
  const s = String(msg || '').toLowerCase();
  return /batteri|saldo|budget|quota|402|authentic|nyckel/i.test(s);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
  { id: 'soft_grunge', label: '🖤 Soft-grunge', desc: 'Kaxig, retsam, vass humor' },
  { id: 'cafe',        label: '☕ Café-flirten', desc: 'Varm, mystisk, snabb humor' },
  { id: 'sunshine',    label: '🌸 Solskens-sötis', desc: 'Ljus, peppande, vass humor' },
  { id: 'artist',      label: '🎨 Konstnärs-rebellen', desc: 'Filosofisk, smygsensuell' },
  { id: 'custom',      label: '✨ Egen', desc: 'Beskriv själv' },
];

const VIBE_LABELS = {
  1: '🌷 Oskyldigt charmigt',
  2: '🦋 Mysigt & kittlande',
  3: '💗 Pussar & kramar',
  4: '🌹 Pirrigt & nära',
  5: '✨ Som mest framåt',
};

function MessageBubble({ msg, name }) {
  const isUser = msg.role === 'user';
  const imgSrc = msg.image_preview || (msg.image_b64 ? `data:${msg.image_mime || 'image/jpeg'};base64,${msg.image_b64}` : null);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`iris-msg-${msg.role}`}>
      <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${
        isUser
          ? 'bg-gradient-to-br from-rose-400 to-pink-500 text-white rounded-br-md shadow-sm'
          : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-rose-100'
      }`}>
        {!isUser && <div className="text-[10px] uppercase font-bold text-rose-500 mb-0.5">{name}</div>}
        {imgSrc && (
          <img
            src={imgSrc}
            alt="bifogad"
            className="rounded-xl max-h-48 max-w-full object-cover mb-1.5 border border-white/40"
          />
        )}
        {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
      </div>
    </div>
  );
}

export default function IrisChat() {
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [diary, setDiary] = useState({ items: [], has_this_week: false });
  const [diaryOpen, setDiaryOpen] = useState(false);
  const [diaryBusy, setDiaryBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeVideoB64, setActiveVideoB64] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoTaskId, setVideoTaskId] = useState(null);
  const [cropFile, setCropFile] = useState(null);   // pending file to crop
  const scrollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, m, d] = await Promise.all([
        axios.get(`${API}/iris/settings`, { withCredentials: true }),
        axios.get(`${API}/iris/messages?limit=80`, { withCredentials: true }),
        axios.get(`${API}/iris/diary`, { withCredentials: true }),
      ]);
      setSettings(s.data);
      setMessages(m.data?.items || []);
      setDiary(d.data || { items: [], has_this_week: false });
    } catch (e) {
      toast.error('Kunde inte ladda Iris');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-open wizard for brand-new users (no messages + no portrait yet)
  useEffect(() => {
    if (!settings) return;
    const isFresh = !settings.has_portrait && (settings.message_count || 0) === 0;
    if (isFresh && !wizardOpen) {
      setWizardOpen(true);
    }
     
  }, [settings?.message_count, settings?.has_portrait]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    // Optimistic append
    setMessages((prev) => [...prev, { role: 'user', text, created_at: new Date().toISOString() }]);
    try {
      const r = await axios.post(`${API}/iris/chat`, { text }, { withCredentials: true });
      const reply = r.data?.reply || '';
      setMessages((prev) => [...prev, { role: 'iris', text: reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Något gick fel';
      toast.error(detail);
      if (looksLikeLlmError(detail) || e?.response?.status === 402 || e?.response?.status === 502) {
        notifyLlmFailure(detail);
      }
      // If LLM budget gone, leave a soft placeholder
      setMessages((prev) => [...prev, { role: 'iris', text: '…verkar som mitt batteri sinade just nu 💔 (LLM-saldot)', created_at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (busy) return;
    setBusy(true);
    const caption = input.trim();
    setInput('');
    const previewUrl = URL.createObjectURL(f);
    setMessages((prev) => [...prev, { role: 'user', text: caption || '[skickade en bild 📸]', image_preview: previewUrl, created_at: new Date().toISOString() }]);
    try {
      const fd = new FormData();
      fd.append('file', f);
      if (caption) fd.append('caption', caption);
      const r = await axios.post(`${API}/iris/chat/image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const reply = r.data?.reply || '';
      setMessages((prev) => [...prev, { role: 'iris', text: reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Iris kunde inte titta på bilden');
      setMessages((prev) => [...prev, { role: 'iris', text: '…jag försökte men kunde inte se bilden riktigt 💔', created_at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openSettings = () => {
    setDraft({ ...(settings || {}) });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    try {
      const r = await axios.post(`${API}/iris/settings`, {
        name: draft.name,
        persona: draft.persona,
        custom_persona: draft.custom_persona,
        vibe_level: Number(draft.vibe_level),
        use_name: draft.use_name,
        hair: draft.hair,
        eyes: draft.eyes,
        body: draft.body,
        style: draft.style,
        vibe_look: draft.vibe_look,
        free_appearance: draft.free_appearance,
        crush_name: draft.crush_name,
        crush_gender: draft.crush_gender,
        crush_traits: draft.crush_traits,
        crush_intensity: draft.crush_intensity != null ? Number(draft.crush_intensity) : undefined,
        crush_status: draft.crush_status,
      }, { withCredentials: true });
      setSettings({ ...settings, ...r.data });
      toast.success('Sparat 💋');
      setSettingsOpen(false);
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const genPortrait = async () => {
    setBusy(true);
    try {
      // Save first so backend has latest appearance
      await axios.post(`${API}/iris/settings`, {
        hair: draft.hair, eyes: draft.eyes, style: draft.style,
        vibe_look: draft.vibe_look, free_appearance: draft.free_appearance,
        name: draft.name,
      }, { withCredentials: true });
      const r = await axios.post(`${API}/iris/portrait`, {}, { withCredentials: true });
      setDraft({ ...draft, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      setSettings({ ...settings, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      toast.success('Porträtt klart 💞');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa porträtt');
    } finally {
      setBusy(false);
    }
  };

  const uploadPortrait = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // allow re-pick same file later
    if (!f) return;
    setCropFile(f);
  };

  const commitPortrait = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', new File([blob], 'portrait.jpg', { type: 'image/jpeg' }));
      const r = await axios.post(`${API}/iris/portrait/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDraft({ ...draft, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      setSettings({ ...settings, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      toast.success('Bilden uppladdad 💞');
      setCropFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte ladda upp');
    } finally {
      setBusy(false);
    }
  };

  const resetChat = async () => {
    if (!window.confirm('Sudda hela konversationen med Iris? 🗑️')) return;
    try {
      await axios.post(`${API}/iris/reset`, {}, { withCredentials: true });
      setMessages([]);
      toast.success('Rensad — fräsch start 💕');
    } catch {/**/}
  };

  const openDiary = async () => {
    setDiaryOpen(true);
    if (!diary.has_this_week && (settings?.crush_name || '').trim()) {
      // Auto-generate the week's entry the first time the user opens it
      setDiaryBusy(true);
      try {
        const r = await axios.post(`${API}/iris/diary/generate`, {}, { withCredentials: true });
        setDiary((d) => ({ ...d, items: [r.data, ...(d.items || [])], has_this_week: true }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Kunde inte hämta veckans update');
      } finally {
        setDiaryBusy(false);
      }
    }
  };

  const markDiaryRead = async (id) => {
    try {
      await axios.post(`${API}/iris/diary/${id}/read`, {}, { withCredentials: true });
      setDiary((d) => ({ ...d, items: d.items.map((it) => it.id === id ? { ...it, read: true } : it) }));
    } catch {/**/}
  };

  const requestVideo = async () => {
    if (videoBusy) return;
    setVideoBusy(true);
    try {
      const r = await axios.post(`${API}/iris/video`, { scene: '' }, { withCredentials: true });
      setVideoTaskId(r.data?.id);
      toast.info('Iris filmar en kort hälsning… (~1-2 min) 💋', { duration: 4500 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte starta video');
      setVideoBusy(false);
    }
  };

  // Poll the iris video status
  useEffect(() => {
    if (!videoTaskId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/iris/video/${videoTaskId}`, { withCredentials: true });
        if (cancelled) return;
        const s = r.data?.status;
        if (s === 'ready' && r.data?.video_b64) {
          setActiveVideoB64(r.data.video_b64);
          toast.success('🎬 Iris skickade ett klipp');
          setVideoTaskId(null);
          setVideoBusy(false);
        } else if (s === 'failed') {
          toast.error('💔 Videon misslyckades (LLM-saldo?)');
          setVideoTaskId(null);
          setVideoBusy(false);
        }
      } catch {/**/}
    };
    const id = setInterval(poll, 7000);
    poll();
    return () => { cancelled = true; clearInterval(id); };
  }, [videoTaskId]);

  if (!settings) {
    return <div className="p-6 text-center text-rose-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }

  const name = settings.name || 'Iris';

  return (
    <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 rounded-3xl border-2 border-rose-200 shadow-sm overflow-hidden" data-testid="iris-chat">
      {/* CSS animations for living portrait */}
      <style>{`
        @keyframes irisBreath { 0%,100%{transform:scale(1)} 50%{transform:scale(1.025)} }
        @keyframes irisBlink { 0%,92%,100%{filter:none} 94%,96%{filter:brightness(0.55) contrast(1.4)} }
        @keyframes irisGlow { 0%,100%{box-shadow:0 0 0 0 rgba(255,150,200,.0)} 50%{box-shadow:0 0 0 8px rgba(255,150,200,.18)} }
        .iris-living { animation: irisBreath 4.5s ease-in-out infinite, irisBlink 6s ease-in-out infinite, irisGlow 3.2s ease-in-out infinite; }
      `}</style>
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-400 via-pink-500 to-fuchsia-500 px-4 py-3 text-white flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner overflow-hidden border-2 border-white/40 iris-living">
            {activeVideoB64 ? (
              <video
                src={`data:video/mp4;base64,${activeVideoB64}`}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                data-testid="iris-avatar-video"
              />
            ) : settings.portrait_b64 ? (
              <img src={`data:${settings.portrait_mime || 'image/png'};base64,${settings.portrait_b64}`} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span>💋</span>
            )}
          </div>
          <div>
            <div className="font-extrabold text-base leading-tight">{name}</div>
            <div className="text-[10px] opacity-90 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" /> Online · {VIBE_LABELS[settings.vibe_level] || ''}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setWizardOpen(true)} data-testid="iris-guide-btn" className="p-2 rounded-full bg-white/20 hover:bg-white/30" title="Iris guidar dig">
            <Sparkles className="w-4 h-4" />
          </button>
          <button onClick={openDiary} data-testid="iris-diary-btn" className="relative p-2 rounded-full bg-white/20 hover:bg-white/30" title="Veckans Mira-update">
            <Mail className="w-4 h-4" />
            {diary.items?.some((it) => !it.read) && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-fuchsia-300 ring-2 ring-pink-500 animate-pulse" />
            )}
          </button>
          <button onClick={openSettings} data-testid="iris-settings-btn" className="p-2 rounded-full bg-white/20 hover:bg-white/30" title="Inställningar">
            <Settings className="w-4 h-4" />
          </button>
          <button onClick={resetChat} data-testid="iris-reset-btn" className="p-2 rounded-full bg-white/20 hover:bg-white/30" title="Rensa">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="px-4 py-3 h-[420px] sm:h-[480px] overflow-y-auto" data-testid="iris-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-rose-400 px-6">
            <Heart className="w-10 h-10 mb-2 fill-rose-200 text-rose-300" />
            <p className="text-sm font-bold text-rose-500">Säg hej till {name} 💋</p>
            <p className="text-xs opacity-80 mt-1 italic">Det här är ditt privata utrymme. Hon kommer ihåg vad ni pratat om.</p>
            <button
              onClick={() => setWizardOpen(true)}
              data-testid="iris-empty-guide-btn"
              className="mt-4 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-extrabold shadow flex items-center gap-1.5 hover:from-rose-600 hover:to-pink-600"
            >
              <Sparkles className="w-3.5 h-3.5" /> Låt Iris guida dig först
            </button>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m} name={name} />
        ))}
        {busy && (
          <div className="flex justify-start mb-2">
            <div className="bg-white text-rose-400 px-3 py-2 rounded-2xl rounded-bl-md text-sm shadow-sm border border-rose-100">
              <span className="inline-flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-300 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-rose-300 animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-rose-300 animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-3 bg-white border-t border-rose-100 flex gap-2 items-end">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={`Säg något till ${name}…`}
          rows={1}
          data-testid="iris-input"
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-rose-200 focus:border-rose-500 outline-none text-sm resize-none max-h-32"
        />
        <button
          onClick={requestVideo}
          disabled={videoBusy}
          data-testid="iris-video-btn"
          title="Be Iris om ett kort videoklipp"
          className="px-3 py-2.5 rounded-2xl bg-gradient-to-br from-fuchsia-400 to-pink-400 hover:from-fuchsia-500 hover:to-pink-500 text-white font-bold disabled:opacity-50 shadow"
        >
          {videoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-base leading-none">🎬</span>}
        </button>
        <label
          data-testid="iris-image-attach-label"
          title="Skicka en bild till Iris"
          className={`px-3 py-2.5 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-400 hover:from-violet-500 hover:to-fuchsia-500 text-white font-bold shadow cursor-pointer flex items-center ${busy ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <span className="text-base leading-none">📸</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={sendImage}
            disabled={busy}
            data-testid="iris-image-attach"
            className="hidden"
          />
        </label>
        <button
          onClick={send}
          disabled={!input.trim() || busy}
          data-testid="iris-send"
          className="px-3 py-2.5 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-bold disabled:opacity-50 shadow flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {/* Settings modal */}
      {settingsOpen && draft && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={() => setSettingsOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border-2 border-rose-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-rose-400 to-pink-500 px-5 py-3 flex items-center justify-between text-white">
              <h3 className="font-extrabold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Iris — inställningar</h3>
              <button onClick={() => setSettingsOpen(false)} data-testid="iris-settings-close" className="text-white/90 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-rose-700">Hennes namn</label>
                <input
                  value={draft.name || ''}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  data-testid="iris-name-input"
                  className="w-full px-3 py-2 rounded-xl border border-rose-200 focus:border-rose-500 outline-none text-sm"
                  placeholder="Iris"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-rose-700">Vad ska hon kalla dig?</label>
                <input
                  value={draft.use_name || ''}
                  onChange={(e) => setDraft({ ...draft, use_name: e.target.value })}
                  data-testid="iris-username-input"
                  className="w-full px-3 py-2 rounded-xl border border-rose-200 focus:border-rose-500 outline-none text-sm"
                  placeholder="(Tomt = hon hittar på smeknamn)"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-rose-700">Persona</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {PERSONAS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setDraft({ ...draft, persona: p.id })}
                      data-testid={`iris-persona-${p.id}`}
                      className={`text-left px-3 py-2 rounded-xl text-sm border-2 transition ${
                        draft.persona === p.id
                          ? 'bg-rose-100 border-rose-400 text-rose-900'
                          : 'bg-white border-gray-200 hover:border-rose-200'
                      }`}
                    >
                      <div className="font-bold">{p.label}</div>
                      <div className="text-[11px] opacity-70">{p.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {draft.persona === 'custom' && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-rose-700">Beskriv egen persona</label>
                  <textarea
                    value={draft.custom_persona || ''}
                    onChange={(e) => setDraft({ ...draft, custom_persona: e.target.value })}
                    rows={3}
                    data-testid="iris-custom-input"
                    className="w-full px-3 py-2 rounded-xl border border-rose-200 focus:border-rose-500 outline-none text-sm"
                    placeholder="t.ex. blyg bokmal, smart humor, läser fantasy..."
                  />
                </div>
              )}

              <div className="space-y-1 pt-2 border-t border-rose-100">
                <label className="text-xs font-extrabold text-rose-700 uppercase tracking-wide">Utseende</label>
                <p className="text-[10px] text-gray-500 italic">Hon refererar till detta i texten — och i porträttet 💋</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-rose-700">Hår</label>
                  <input
                    value={draft.hair || ''}
                    onChange={(e) => setDraft({ ...draft, hair: e.target.value })}
                    data-testid="iris-hair-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. långt vågigt mörkbrunt"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-rose-700">Ögon</label>
                  <input
                    value={draft.eyes || ''}
                    onChange={(e) => setDraft({ ...draft, eyes: e.target.value })}
                    data-testid="iris-eyes-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. varma bruna"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Kropp</label>
                  <input
                    value={draft.body || ''}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    data-testid="iris-body-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. smal, kurvig, atletisk, mjuka former…"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Kläder / stil</label>
                  <input
                    value={draft.style || ''}
                    onChange={(e) => setDraft({ ...draft, style: e.target.value })}
                    data-testid="iris-style-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. oversized hoodie + doc martens, klänning…"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Övergripande vibe</label>
                  <input
                    value={draft.vibe_look || ''}
                    onChange={(e) => setDraft({ ...draft, vibe_look: e.target.value })}
                    data-testid="iris-vibelook-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. soft-grunge, bohemiskt, elegant…"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Extra detaljer</label>
                  <textarea
                    value={draft.free_appearance || ''}
                    onChange={(e) => setDraft({ ...draft, free_appearance: e.target.value })}
                    rows={2}
                    data-testid="iris-freeapp-input"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. fräknar, tatueringar, läppstift…"
                  />
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {draft.portrait_b64 && (
                  <img
                    src={`data:${draft.portrait_mime || 'image/png'};base64,${draft.portrait_b64}`}
                    alt="porträtt"
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-rose-200 shadow-sm"
                  />
                )}
                <button
                  onClick={genPortrait}
                  disabled={busy}
                  data-testid="iris-portrait-btn"
                  className="flex-1 py-2 rounded-xl bg-gradient-to-r from-fuchsia-400 to-rose-400 text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {draft.portrait_b64 ? 'Skapa nytt porträtt' : 'Skapa porträtt med AI ✨'}
                </button>
                <label
                  data-testid="iris-portrait-upload-label"
                  className={`py-2 px-3 rounded-xl bg-white border-2 border-rose-300 text-rose-700 text-xs font-bold flex items-center gap-1 cursor-pointer hover:bg-rose-50 ${busy ? 'opacity-50 pointer-events-none' : ''}`}
                  title="Ladda upp egen bild"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Ladda upp
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={uploadPortrait}
                    disabled={busy}
                    data-testid="iris-portrait-upload"
                    className="hidden"
                  />
                </label>
              </div>

              <div className="space-y-1 pt-2 border-t border-rose-100">
                <label className="text-xs font-extrabold text-rose-700 uppercase tracking-wide">Kompis hon är lite kär i 💞</label>
                <p className="text-[10px] text-gray-500 italic">Skapar hemlig romcom-spänning — hon kan nämna kompisen ibland 😏</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-rose-700">Kompisens namn</label>
                  <input
                    value={draft.crush_name || ''}
                    onChange={(e) => setDraft({ ...draft, crush_name: e.target.value })}
                    data-testid="iris-crush-name"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="Mira"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-rose-700">Kön</label>
                  <select
                    value={draft.crush_gender || 'tjej'}
                    onChange={(e) => setDraft({ ...draft, crush_gender: e.target.value })}
                    data-testid="iris-crush-gender"
                    className="w-full px-2 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs bg-white"
                  >
                    <option value="tjej">Tjej</option>
                    <option value="kille">Kille</option>
                    <option value="annat">Annat</option>
                  </select>
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Hur är kompisen?</label>
                  <input
                    value={draft.crush_traits || ''}
                    onChange={(e) => setDraft({ ...draft, crush_traits: e.target.value })}
                    data-testid="iris-crush-traits"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. lång, rufsigt blont hår, alltid lite för flörtig…"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Status (hur ligger det till)</label>
                  <input
                    value={draft.crush_status || ''}
                    onChange={(e) => setDraft({ ...draft, crush_status: e.target.value })}
                    data-testid="iris-crush-status"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. smyger fortfarande, vi pussades en gång, hen har en partner…"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700 flex items-center justify-between">
                    <span>Hur kär är hon?</span>
                    <span className="text-rose-500 font-extrabold">{['🌷 nyfiken','💗 liten crush','💞 seriöst kär','🔥 helt på','✨ hopplöst betagen'][(draft.crush_intensity||3)-1]}</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={draft.crush_intensity || 3}
                    onChange={(e) => setDraft({ ...draft, crush_intensity: Number(e.target.value) })}
                    data-testid="iris-crush-intensity"
                    className="w-full accent-fuchsia-500"
                  />
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-rose-100">
                <label className="text-xs font-extrabold text-rose-700 uppercase tracking-wide">Mellan er två 🔥</label>
                <p className="text-[10px] text-gray-500 italic">Stämningen mellan Iris och dig — för pirret</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-rose-700">Din status</label>
                  <select
                    value={draft.user_status || ''}
                    onChange={(e) => setDraft({ ...draft, user_status: e.target.value })}
                    data-testid="iris-user-status"
                    className="w-full px-2 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs bg-white"
                  >
                    <option value="">— välj —</option>
                    <option value="singel">Singel</option>
                    <option value="i förhållande">I förhållande</option>
                    <option value="gift">Gift</option>
                    <option value="komplicerat">Komplicerat</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-[11px] font-bold text-rose-700 cursor-pointer pt-5">
                  <input
                    type="checkbox"
                    checked={!!draft.mutual_attraction}
                    onChange={(e) => setDraft({ ...draft, mutual_attraction: e.target.checked })}
                    data-testid="iris-mutual"
                    className="accent-rose-500"
                  />
                  💋 Vi är tända på varandra
                </label>
                <div className="space-y-1 col-span-2">
                  <label className="text-[11px] font-bold text-rose-700">Fri text (kontext)</label>
                  <input
                    value={draft.user_status_freetext || ''}
                    onChange={(e) => setDraft({ ...draft, user_status_freetext: e.target.value })}
                    data-testid="iris-userstatus-free"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-rose-200 focus:border-rose-500 outline-none text-xs"
                    placeholder="t.ex. nyligen separerad, partner vet inte om er, långdistans…"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-rose-700 flex items-center justify-between">
                  <span>Vibe-nivå</span>
                  <span className="text-rose-500 font-extrabold">{VIBE_LABELS[draft.vibe_level] || ''}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="1"
                  value={draft.vibe_level || 2}
                  onChange={(e) => setDraft({ ...draft, vibe_level: Number(e.target.value) })}
                  data-testid="iris-vibe-slider"
                  className="w-full accent-rose-500"
                />
                <div className="flex justify-between text-[9px] text-rose-400 px-1">
                  <span>oskyldigt</span>
                  <span>kittlande</span>
                  <span>lekfullt</span>
                  <span>djärvt</span>
                  <span>framåt</span>
                </div>
                <p className="text-[10px] text-gray-500 italic">
                  Aldrig explicit eller plumpt — bara stämning och kemi 💞
                </p>
              </div>
              <button
                onClick={saveSettings}
                data-testid="iris-settings-save"
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-sm shadow hover:from-rose-600 hover:to-pink-600"
              >
                Spara
              </button>
            </div>
          </div>
        </div>
      )}

      {wizardOpen && (
        <IrisSetupWizard
          initial={settings}
          onClose={() => setWizardOpen(false)}
          onDone={async () => {
            setWizardOpen(false);
            await loadAll();
            toast.success('Iris är redo för dig 💞');
          }}
        />
      )}

      {cropFile && (
        <PortraitCropModal
          file={cropFile}
          title="Beskär Iris-bild"
          accent="rose"
          onCancel={() => setCropFile(null)}
          onConfirm={commitPortrait}
        />
      )}
    </div>
  );
}
